import { useState, useEffect } from "react";

import { format } from "date-fns";
import { Bookmark, Trash2, Edit2, Check, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { toast } from "@/components/ui/sonner";
import { supabase } from "@/integrations/supabase/client";


interface Message {
  id: string;
  role: "user" | "assistant";
  content: string;
  created_at: string;
}

interface BookmarkWithMessage {
  id: string;
  message_id: string;
  note: string | null;
  created_at: string;
  message: Message;
}

interface BookmarkPanelProps {
  sessionId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onJumpToMessage: (messageId: string) => void;
}

export const BookmarkPanel = ({ sessionId, open, onOpenChange, onJumpToMessage }: BookmarkPanelProps) => {
  const [bookmarks, setBookmarks] = useState<BookmarkWithMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingBookmarkId, setEditingBookmarkId] = useState<string | null>(null);
  const [editingNote, setEditingNote] = useState("");

  useEffect(() => {
    if (open) {
      fetchBookmarks();
    }
  }, [open, sessionId]);

  const fetchBookmarks = async () => {
    try {
      setLoading(true);
      
      // Get all messages for this session
      const { data: sessionMessages, error: messagesError } = await supabase
        .from("messages")
        .select("id")
        .eq("session_id", sessionId);

      if (messagesError) throw messagesError;

      const messageIds = sessionMessages?.map((m) => m.id) || [];

      if (messageIds.length === 0) {
        setBookmarks([]);
        return;
      }

      // Get bookmarks for these messages
      const { data: bookmarksData, error: bookmarksError } = await supabase
        .from("message_bookmarks")
        .select("*")
        .in("message_id", messageIds)
        .order("created_at", { ascending: false });

      if (bookmarksError) throw bookmarksError;

      // Get message details for each bookmark
      const bookmarksWithMessages = await Promise.all(
        (bookmarksData || []).map(async (bookmark) => {
          const { data: message } = await supabase
            .from("messages")
            .select("*")
            .eq("id", bookmark.message_id)
            .single();

          return {
            ...bookmark,
            message: message as Message,
          };
        })
      );

      setBookmarks(bookmarksWithMessages.filter((b) => b.message));
    } catch (error) {
      console.error("Error fetching bookmarks:", error);
      toast.error("Failed to load bookmarks");
    } finally {
      setLoading(false);
    }
  };

  const startEditingNote = (bookmark: BookmarkWithMessage) => {
    setEditingBookmarkId(bookmark.id);
    setEditingNote(bookmark.note || "");
  };

  const cancelEditing = () => {
    setEditingBookmarkId(null);
    setEditingNote("");
  };

  const saveNote = async (bookmarkId: string) => {
    try {
      const { error } = await supabase
        .from("message_bookmarks")
        .update({ note: editingNote.trim() || null })
        .eq("id", bookmarkId);

      if (error) throw error;

      setBookmarks((prev) =>
        prev.map((b) =>
          b.id === bookmarkId ? { ...b, note: editingNote.trim() || null } : b
        )
      );

      setEditingBookmarkId(null);
      setEditingNote("");
      toast.success("Note updated");
    } catch (error) {
      console.error("Error updating note:", error);
      toast.error("Failed to update note");
    }
  };

  const deleteBookmark = async (bookmarkId: string) => {
    try {
      const { error } = await supabase
        .from("message_bookmarks")
        .delete()
        .eq("id", bookmarkId);

      if (error) throw error;

      setBookmarks((prev) => prev.filter((b) => b.id !== bookmarkId));
      toast.success("Bookmark removed");
    } catch (error) {
      console.error("Error deleting bookmark:", error);
      toast.error("Failed to remove bookmark");
    }
  };

  const handleJumpToMessage = (messageId: string) => {
    onJumpToMessage(messageId);
    onOpenChange(false);
  };

  const truncateContent = (content: string, maxLength: number = 100) => {
    if (content.length <= maxLength) return content;
    return content.slice(0, maxLength) + "...";
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-[400px] sm:w-[540px]">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Bookmark className="h-5 w-5" />
            Bookmarked Messages
          </SheetTitle>
          <SheetDescription>
            {bookmarks.length === 0
              ? "No bookmarks yet. Bookmark important messages to save them here."
              : `${bookmarks.length} bookmark${bookmarks.length === 1 ? "" : "s"}`}
          </SheetDescription>
        </SheetHeader>

        <ScrollArea className="h-[calc(100vh-120px)] mt-6">
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Loading...</div>
          ) : bookmarks.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Bookmark className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No bookmarks yet</p>
            </div>
          ) : (
            <div className="space-y-4">
              {bookmarks.map((bookmark) => (
                <div
                  key={bookmark.id}
                  className="border rounded-lg p-4 space-y-3 hover:bg-accent/5 transition-colors"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span
                          className={`text-xs font-medium px-2 py-0.5 rounded ${
                            bookmark.message.role === "user"
                              ? "bg-primary/10 text-primary"
                              : "bg-secondary/50 text-secondary-foreground"
                          }`}
                        >
                          {bookmark.message.role === "user" ? "You" : "Assistant"}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {format(new Date(bookmark.created_at), "MMM d, h:mm a")}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {truncateContent(bookmark.message.content)}
                      </p>
                    </div>
                  </div>

                  {editingBookmarkId === bookmark.id ? (
                    <div className="space-y-2">
                      <Input
                        placeholder="Add a note (optional)..."
                        value={editingNote}
                        onChange={(e) => setEditingNote(e.target.value)}
                        className="text-sm"
                        autoFocus
                      />
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={cancelEditing}
                        >
                          <X className="h-3 w-3 mr-1" />
                          Cancel
                        </Button>
                        <Button size="sm" onClick={() => saveNote(bookmark.id)}>
                          <Check className="h-3 w-3 mr-1" />
                          Save
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      {bookmark.note && (
                        <p className="text-sm bg-muted p-2 rounded italic">
                          {bookmark.note}
                        </p>
                      )}
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleJumpToMessage(bookmark.message_id)}
                          className="flex-1"
                        >
                          Jump to message
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => startEditingNote(bookmark)}
                        >
                          <Edit2 className="h-3 w-3" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => deleteBookmark(bookmark.id)}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
};
